import { sensorsUrl } from "../api/endpoints";
import axios from "axios";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactSlider from "react-slider";

import "./Slider.css";
import { resolveValueColorFromStops } from "./liquidShared";

const Slider = ({
  title,
  transparentBackground,
  min,
  max,
  value,
  step,
  unit,
  color,
  buttonTextColor,
  buttonBgColor,
  valueColor,
  changeColorBasedOnValue = false,
  colorGrading = false,
  colors = [],
  datastreamColor,
draggable,
  pin,
  did,
  tid,
  direction,
  contentAlignment,
  onValueCommit,
}) => {
  const containerRef = useRef(null);

  const resolveControlColor = (v) => {
    const s = String(v || '').trim();
    return s ? s : null;
  };

  const resolvedColor = useMemo(() => {
    const rawColor = resolveControlColor(color);
    const normalizedColor = rawColor ? rawColor.toLowerCase() : '';
    if (normalizedColor === '#c52184') return '#f337b4';
    return rawColor || resolveControlColor(datastreamColor) || "#f337b4";
  }, [color, datastreamColor]);

  const resolvedBtnTextColor = useMemo(() => {
    return (
      resolveControlColor(buttonTextColor) ||
      '#3ccfe6'
    );
  }, [buttonTextColor]);

  const resolvedBtnBgColor = useMemo(() => {
    return (
      resolveControlColor(buttonBgColor) ||
      'rgba(34, 88, 113, 0.48)'
    );
  }, [buttonBgColor]);

  const resolvedValueColor = useMemo(() => {
    return (
      resolveControlColor(valueColor) ||
      '#ffffff'
    );
  }, [valueColor]);

  const toNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));


  const numericMin = useMemo(() => toNumber(min, 0), [min]);
  const numericMax = useMemo(
    () => toNumber(max, numericMin + 100),
    [max, numericMin]
  );
  const numericStep = useMemo(() => {
    const s = toNumber(step, 1);
    return s > 0 ? s : 1;
  }, [step]);


  const normalizedExternalValue = useMemo(
    () => clamp(toNumber(value, numericMin), numericMin, numericMax),
    [value, numericMin, numericMax]
  );

  const [sliderValue, setSliderValue] = useState(normalizedExternalValue);
  const [cursorState, setCursorState] = useState("idle");
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const pendingCommitTimeoutRef = useRef(null);
  const pendingCommitValueRef = useRef(null);
  const lastExternalValueRef = useRef(normalizedExternalValue);

  const clearPendingCommitTimeout = useCallback(() => {
    if (!pendingCommitTimeoutRef.current) return;
    clearTimeout(pendingCommitTimeoutRef.current);
    pendingCommitTimeoutRef.current = null;
  }, []);

  const syncToExternalValue = useCallback((nextValue) => {
    setSliderValue(clamp(toNumber(nextValue, numericMin), numericMin, numericMax));
  }, [numericMin, numericMax]);

  useEffect(() => {
    const previousExternalValue = lastExternalValueRef.current;
    const valueTolerance = Math.max(1e-6, numericStep / 1000);
    const hasExternalChanged =
      Math.abs(normalizedExternalValue - previousExternalValue) > valueTolerance;

    lastExternalValueRef.current = normalizedExternalValue;

    if (isUserInteracting) {
      return;
    }

    const pendingCommitValue = pendingCommitValueRef.current;
    if (pendingCommitValue !== null) {
      const matchesPending =
        Math.abs(normalizedExternalValue - pendingCommitValue) <= valueTolerance;

      if (!matchesPending && !hasExternalChanged) {
        return;
      }

      pendingCommitValueRef.current = null;
      clearPendingCommitTimeout();
    }

    setSliderValue(normalizedExternalValue);
  }, [
    clearPendingCommitTimeout,
    isUserInteracting,
    normalizedExternalValue,
    numericStep,
  ]);

  useEffect(() => {
    return () => {
      clearPendingCommitTimeout();
    };
  }, [clearPendingCommitTimeout]);

  const dynamicValueColor = useMemo(() => {
    return resolveValueColorFromStops({
      value: clamp(sliderValue, numericMin, numericMax),
      baseColor: null,
      stops: colors,
      enabled: changeColorBasedOnValue,
      grading: colorGrading,
    });
  }, [
    changeColorBasedOnValue,
    colors,
    sliderValue,
    numericMin,
    numericMax,
    colorGrading,
  ]);

  const activeValueColor = useMemo(
    () => dynamicValueColor || resolvedValueColor,
    [dynamicValueColor, resolvedValueColor]
  );


  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.setProperty("--slider-color", resolvedColor);
    containerRef.current.style.setProperty("--slider-control-color", resolvedColor);

    if (resolvedBtnTextColor) {
      containerRef.current.style.setProperty("--slider-button-color", resolvedBtnTextColor);
      containerRef.current.style.setProperty("--slider-button-border-color", resolvedBtnTextColor);
    } else {
      containerRef.current.style.removeProperty("--slider-button-color");
      containerRef.current.style.removeProperty("--slider-button-border-color");
    }

    if (resolvedBtnBgColor) {
      containerRef.current.style.setProperty("--slider-button-bg", resolvedBtnBgColor);
    } else {
      containerRef.current.style.removeProperty("--slider-button-bg");
    }

    if (activeValueColor) {
      containerRef.current.style.setProperty("--slider-percentage-color", activeValueColor);
    } else {
      containerRef.current.style.removeProperty("--slider-percentage-color");
    }
  }, [resolvedColor, resolvedBtnTextColor, resolvedBtnBgColor, activeValueColor]);


  const handleSliderChange = (newValue) => {
    const next = clamp(toNumber(newValue, numericMin), numericMin, numericMax);
    setSliderValue(next);
  };

  const handleSliderCommit = (finalValue) => {
    const next = clamp(toNumber(finalValue, numericMin), numericMin, numericMax);
    onValueCommit?.(next);
    if (!did) return;

    pendingCommitValueRef.current = next;
    clearPendingCommitTimeout();
    pendingCommitTimeoutRef.current = setTimeout(() => {
      pendingCommitValueRef.current = null;
      syncToExternalValue(lastExternalValueRef.current);
    }, 1500);

    const body = {
      device: did,
      template: tid,
      data: { [pin]: next.toFixed(1) },
    };

    axios
      .post(sensorsUrl(`control`), body, {
        headers: { "Content-Type": "application/json" },
      })
      .catch(() => {
        pendingCommitValueRef.current = null;
        clearPendingCommitTimeout();
        syncToExternalValue(lastExternalValueRef.current);
      });
  };


  const stepUp = () => {
    const next = sliderValue + numericStep;
    handleSliderChange(next);
    handleSliderCommit(next);
  };

  const stepDown = () => {
    const next = sliderValue - numericStep;
    handleSliderChange(next);
    handleSliderCommit(next);
  };


  const isRTL = direction === "rtl";
  const normalizedContentAlignment = useMemo(() => {
    const value = String(contentAlignment || "auto").trim().toLowerCase();
    return ["auto", "top", "center", "bottom", "title-line"].includes(value) ? value : "auto";
  }, [contentAlignment]);


  const cursorClass = draggable
    ? cursorState === "grabbing"
      ? "slider-widget--grabbing"
      : "slider-widget--grab"
    : "slider-widget--auto";

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "slider-widget",
        cursorClass,
        `slider-widget--align-${normalizedContentAlignment}`,
        isUserInteracting ? "slider-widget--active" : "",
        transparentBackground ? "slider-widget--transparent" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={() => draggable && setCursorState("grabbing")}
      onMouseUp={() => draggable && setCursorState("grab")}
      onMouseLeave={() => draggable && setCursorState("grab")}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest(".widget")?.id || "";
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `slider;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith("base") ? "add" : "move";

        e.dataTransfer.setData("text/plain", dragData);
      }}
    >
      <h3 className="slider-widget__title">{title}</h3>

      <div className="slider-widget__inner">
        <div className="slider-widget__row slider-row">
          <div
            className={[
              "the-slider",
              "slider-widget__controlRow",
              isRTL ? "slider-widget__controlRow--rtl" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >

            <button className="slider-widget__btn" onClick={stepDown} title="Decrease" type="button">
              -
            </button>

            <ReactSlider
              className="react-slider"
              trackClassName="react-track"
              thumbClassName="react-thumb"
              renderThumb={(props, state) => (
                <div {...props}>
                  <span className="slider-widget__thumbBubble">
                    {Number(state.valueNow).toFixed(Number.isInteger(numericStep) ? 0 : 1)}
                  </span>
                </div>
              )}
              value={sliderValue}
              min={numericMin}
              max={numericMax}
              step={numericStep}
              onBeforeChange={() => setIsUserInteracting(true)}
              onChange={handleSliderChange}
              onAfterChange={(finalValue) => {
                setIsUserInteracting(false);
                handleSliderCommit(finalValue);
              }}
              dir={direction}
              invert={isRTL}
              ariaLabel={title ? `Control for ${title}` : "Value slider"}
            />

   
            <button className="slider-widget__btn" onClick={stepUp} title="Increase" type="button">
              +
            </button>
          </div>
        </div>

        <div className="slider-widget__percentageWrap">
          <div className="slider-widget__percentage">
            {sliderValue.toFixed(1)} {unit}
          </div>
        </div>

        <div className="slider-widget__limits">
          <span>{`Min:${numericMin}`}</span>
          <span>{`Max:${numericMax}`}</span>
        </div>
      </div>
    </div>
  );
};

export default Slider;
